import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import camelCase from 'lodash/camelCase';
import { Validation } from 'calidation';
import { getValidations } from '@FLOW_V2_FLOW/components/Contents/VerifyDetails/verifyDetailsValidations';
import { defaultLettersAndSymbolsRegexMatch } from '@lib/validations/vefifyDetailsFormValidations';
import classNames from 'classnames';
import { camelCaseToLabel } from '@lib/Utils';
import { isDobUneditable } from '@lib/utils/v2/isDobUneditable';
import { isCardNumberFieldVisible } from '@lib/utils/cardNumberField';
import { getResidentalAddressLabel } from '@lib/addressfieldLabel';
import { getCountryLabelFromIso2Code } from '@lib/countryUtils';
import { Input, DatePicker } from '@lib/components/v2/Form';
import { localizedString } from '@languages';
import classes from './Datacheck.style.module.scss';
import Loading from './Datacheck.Loader';
import { AddressFinder } from '../..';

const {
  FLOW_V2_VERIFY_DETAILS_HEADING,
  FLOW_V2_UNABLE_TO_EDIT_DOB_FOR_SECOND_DOCUMENTS = [],
  FLOW_V2_DIGITAL_DL_HIDE_CARD_NUMBER_FIELD = false,
  FLOW_V2_DATEPICKER_FORMAT
} = process.env;

const Datacheck = ({
  pageTitle = FLOW_V2_VERIFY_DETAILS_HEADING || localizedString('checkYourIdDetails'),
  isLoading = false,
  data = {},
  onChange,
  showDetails = false,
  showDetailedAddress,
  onShowDetailed
}) => {
  if (isLoading && !showDetails) {
    return <Loading />;
  }

  const [inputs, setInputs] = useState({});
  useEffect(() => {
    setInputs(data);
  }, []);

  const formValidations = getValidations();

  const handleChange = (field, value) => {
    const params = { ...inputs };
    params[field] = value;
    setInputs(params);
    onChange(field, value);
  };

  const idType = data.cardType || '';
  const datePickerProps = {
    displayedDateFormat: FLOW_V2_DATEPICKER_FORMAT
  };

  if (idType && idType.match(/MEDICARE/i)) {
    datePickerProps.displayedDateFormat = 'MM-YYYY';
  }

  const fields = [];
  const excluded = ['countryCode', 'cardType', 'documentId', 'state', 'gender'];

  if (
    !isCardNumberFieldVisible({
      idType,
      countryOfIssue: getCountryLabelFromIso2Code(data.countryCode),
      skipDigitalDriverLicence: FLOW_V2_DIGITAL_DL_HIDE_CARD_NUMBER_FIELD
    })
  ) {
    excluded.push('cardNumber');
  }

  const isDobDisabled = isDobUneditable({
    countryCodeIso2: data.countryCode,
    idType,
    restrictedSecondDocsList: FLOW_V2_UNABLE_TO_EDIT_DOB_FOR_SECOND_DOCUMENTS
  });

  Object.keys(data).forEach((field) => {
    if (!excluded.includes(field)) {
      if (field.match(/date/i)) {
        fields.push({
          id: field,
          label: camelCaseToLabel(field),
          value: data[field],
          date: true,
          disabled: field.match(/dateOfBirth/i) && isDobDisabled
        });
      } else if (field.match(/address/i)) {
        fields.push({
          id: field,
          label: getResidentalAddressLabel(),
          value: data[field],
          addressFinder: true
        });
      } else {
        fields.push({
          id: field,
          label: camelCaseToLabel(field),
          value: data[field],
          type: 'text'
        });
      }
    }
  });

  /**
   * Generate form fields.
   */
  const generateFields = fields.map((field, i) => {
    const { id, label, value, date, addressFinder, disabled } = field;
    const labelDataTestId = `datacheck-field-${i}-lbl`;
    const dataTestId = `datacheck-field-${i}`;

    const idCamelCased = camelCase(id);
    let validationRule;

    if (formValidations[idCamelCased]) {
      validationRule = { [id]: { ...formValidations[idCamelCased][idCamelCased] } };
    } else {
      validationRule = {
        [id]: {
          isRequired: `${label} ${localizedString('isRequired')}.`,
          isRegexMatch: defaultLettersAndSymbolsRegexMatch
        }
      };
    }

    // if its date
    if (date) {
      const restProps = {
        id,
        label,
        value,
        disabled
      };
      return (
        <div key={id} className={classNames('', classes.userDetailRow)}>
          <span data-testid={labelDataTestId}>{field.label}</span>
          <Validation config={validationRule} initialValues={{ [id]: value }}>
            {({ dirty, errors: formErrors, setField }) => {
              return (
                <DatePicker
                  key={id}
                  {...datePickerProps}
                  className={classNames(classes.input)}
                  hasError={dirty[id] ? formErrors[id] : null}
                  onChange={(value) => {
                    handleChange(id, value);
                    setField({ [id]: value });
                  }}
                  {...restProps}
                  dataTestId={`${dataTestId}-datePicker`}
                />
              );
            }}
          </Validation>
        </div>
      );
    }
    if (addressFinder) {
      const { FLOW_V2_FORCE_ADDRESS_VALIDATION_COUNTRY_ISO2 } = process.env;

      let countryCodeIso2ToVerify;
      if (value && value.manual) {
        countryCodeIso2ToVerify = value.selectedManualCountryCode
          ? value.selectedManualCountryCode
          : 'AU';
      } else {
        countryCodeIso2ToVerify = FLOW_V2_FORCE_ADDRESS_VALIDATION_COUNTRY_ISO2 || data.countryCode;
      }

      return (
        <div key={id} className={classNames('', classes.userDetailRow)}>
          <span data-testid={labelDataTestId} className={classes['address-label-span']}>
            {field.label}
          </span>
          <AddressFinder
            inputType="textarea"
            data={value}
            showDetailed={showDetailedAddress || value.manual}
            onShowDetailed={onShowDetailed}
            countryCodeIso2ToVerify={countryCodeIso2ToVerify}
            onChange={(value) => onChange(id, value)}
            dataTestId={`${dataTestId}-search`}
          />
        </div>
      );
    }
    return (
      <div key={id} className={classNames('', classes.userDetailRow)}>
        <span data-testid={labelDataTestId}>{field.label}</span>
        <Validation config={validationRule} initialValues={{ [id]: value }}>
          {({ dirty, errors: formErrors, setField }) => {
            return (
              <Input
                key={id}
                placeholder={field.label}
                paddingLeft30
                className={classNames(classes.input)}
                hasError={dirty[id] ? formErrors[id] : null}
                onChange={(value) => {
                  handleChange(id, value);
                  setField({ [id]: value });
                }}
                {...field}
                dataTestId={`${dataTestId}-txt`}
              />
            );
          }}
        </Validation>
      </div>
    );
  });

  return (
    <div className={classes.wrapper}>
      <div className={classes.heading} data-testid="datacheck-heading">
        {pageTitle}
      </div>
      <div className={classes.container}>{generateFields}</div>
    </div>
  );
};

Datacheck.propTypes = {
  data: PropTypes.object,
  pageTitle: PropTypes.string,
  isLoading: PropTypes.bool,
  onChange: PropTypes.func,
  showDetails: PropTypes.bool,
  showDetailedAddress: PropTypes.bool,
  onShowDetailed: PropTypes.func
};

Datacheck.defaultProps = {
  data: {}
};

export default Datacheck;
